import { camelcaseKeys } from '@shared-runtime/utils/keys';

import type { FiatBankAccountsResult } from './apiSchema';
import {
  BankAccountDisplayStatusEnum,
  FiatBankAccountStatusEnum,
  type BankAccountDisplayStatus,
  type FiatBankAccountsResponse,
  type FiatBankAccountStatus,
} from './schema';

const getTimeString = (timestamp: number): string => {
  const date = new Date(timestamp);
  return date.toISOString();
};

const API_BANK_NETWORK_DISPLAY_NAME: Record<string, string> = {
  usd_fedwire: 'FEDWIRE',
  us_ach: 'ACH',
  usd_swift: 'SWIFT',
  usd_cubix: 'CUBIX',
  usd_lynq: 'LYNQ',
  openpayd_exchange_sepa: 'SEPA',
  uk_fps: 'FPS',
  aed_ipi: 'IPI/RTGS',
};

const statusMapping: Partial<
  Record<FiatBankAccountStatus, BankAccountDisplayStatus>
> = {
  [FiatBankAccountStatusEnum.Escalated]: BankAccountDisplayStatusEnum.Failed,
  [FiatBankAccountStatusEnum.Rejected]: BankAccountDisplayStatusEnum.Rejected,
  [FiatBankAccountStatusEnum.Activated]: BankAccountDisplayStatusEnum.Verified,
  [FiatBankAccountStatusEnum.Expired]: BankAccountDisplayStatusEnum.Expired,
};

const normalizeStatus = (status: string): string => {
  return (
    statusMapping[status as FiatBankAccountStatus] ||
    BankAccountDisplayStatusEnum.Pending
  );
};

export function normalizeFiatBankAccounts(
  data: FiatBankAccountsResult,
): FiatBankAccountsResponse {
  const fiatBankAccounts = camelcaseKeys(data.data).map((bank: any) => {
    const {
      bankName,
      accountIdentifierValue,
      supportedPaymentNetworks,
      currency,
      createdAt,
      status,
      bankDetails,
      id,
      bankIdentifierValue,
    } = bank;
    const paymentNetwork = supportedPaymentNetworks[0];
    const paymentNetworkDisplayName =
      API_BANK_NETWORK_DISPLAY_NAME[paymentNetwork] || paymentNetwork;
    const statusDisplay = normalizeStatus(status) as BankAccountDisplayStatus;
    const isCubix = paymentNetwork === 'usd_cubix';

    return {
      label: bankName ? `${bankName} (${paymentNetworkDisplayName})` : '-',
      bankName,
      bankNumber: isCubix ? bankIdentifierValue : accountIdentifierValue,
      paymentNetwork,
      paymentNetworkDisplayName,
      supportedPaymentNetworks,
      currency,
      createdAt,
      createdAtDisplay: getTimeString(new Date(createdAt).getTime()),
      status,
      statusDisplay,
      bankDetails,
      id,
      _raw: bank,
    };
  });

  const verifiedStatus = [FiatBankAccountStatusEnum.Activated];
  const failedStatus = [
    FiatBankAccountStatusEnum.Escalated,
    FiatBankAccountStatusEnum.Rejected,
    FiatBankAccountStatusEnum.Expired,
  ];
  const mapToDisplayStatus = (status: FiatBankAccountStatus) =>
    verifiedStatus.includes(status as FiatBankAccountStatusEnum)
      ? BankAccountDisplayStatusEnum.Verified
      : BankAccountDisplayStatusEnum.Pending;

  const validBankAccounts = fiatBankAccounts
    .filter((account: any) => !failedStatus.includes(account.status))
    .map((account: any) => {
      const statusDisplay = mapToDisplayStatus(account.status);

      return {
        ...account,
        statusDisplay,
      };
    });

  return {
    fiatBankAccounts,
    validBankAccounts,
  };
}
